import type { SchoolDetailsCardProps } from "@components/SchoolRegistration/Cards/SchoolDetailsCard";

export interface SchoolData extends SchoolDetailsCardProps {
  id: string;
  phoneNumber: string;
}

export interface PaginatedSchoolResponse {
  data: SchoolData[];
  cursor: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface SchoolSearchParams {
  cursor?: number;
  limit?: number;
  search?: string;
}

// Generate realistic school names with variety
const schoolNameTemplates = [
  "Government High School",
  "Municipal Primary School",
  "State Secondary School",
  "Public Elementary School",
  "Government Higher Secondary School",
  "Municipal Middle School",
  "State Primary School",
  "Government Elementary School",
  "Public High School",
  "Municipal Secondary School",
  "Government Middle School",
  "State Higher Secondary School",
  "Public Primary School",
  "Government Secondary School",
  "Municipal High School",
];

const locationSuffixes = [
  "Nagar",
  "Puram",
  "Ganj",
  "Bad",
  "Abad",
  "Garh",
  "Kote",
  "Pet",
  "Wada",
  "Colony",
  "Layout",
  "Extension",
  "Cross",
  "Main",
  "Circle",
  "Junction",
  "Road",
  "Street",
  "Park",
  "Garden",
  "Hills",
  "Valley",
  "Ridge",
  "Corner",
  "Square",
  "Plaza",
];

const states = [
  "Karnataka",
  "Maharashtra",
  "Tamil Nadu",
  "Gujarat",
  "Rajasthan",
  "Uttar Pradesh",
  "West Bengal",
  "Madhya Pradesh",
  "Odisha",
  "Bihar",
  "Assam",
  "Punjab",
  "Haryana",
  "Kerala",
  "Jharkhand",
  "Chhattisgarh",
  "Uttarakhand",
  "Himachal Pradesh",
];

const generateDistrictName = (state: string, index: number): string => {
  const districtPrefixes = ["North", "South", "East", "West", "Central"];
  const districtSuffixes = ["District", "Rural", "Urban", "Metro"];

  if (index % 3 === 0) {
    return `${districtPrefixes[index % districtPrefixes.length]} ${state}`;
  } else if (index % 3 === 1) {
    return `${state} ${districtSuffixes[index % districtSuffixes.length]}`;
  } else {
    return `${state.split(" ")[0]} District`;
  }
};

const generateCityName = (_state: string, index: number): string => {
  const cityNames = [
    "Rajpur",
    "Krishnagiri",
    "Mandya",
    "Tumkur",
    "Mysore",
    "Hubli",
    "Belgaum",
    "Gulbarga",
    "Bijapur",
    "Shimoga",
    "Mangalore",
    "Udupi",
    "Hassan",
    "Chikmagalur",
    "Kodagu",
    "Raichur",
    "Koppal",
    "Gadag",
    "Haveri",
    "Chitradurga",
    "Davanagere",
    "Ballari",
    "Bidar",
    "Kalaburagi",
    "Yadgir",
    "Vijayapura",
    "Bagalkot",
    "Dharwad",
    "Uttara Kannada",
    "Dakshina Kannada",
    "Chamarajanagar",
    "Mandya",
  ];

  return cityNames[index % cityNames.length];
};

const generateAddress = (
  _schoolName: string,
  city: string,
  index: number
): string => {
  const streetNumbers = [12, 23, 45, 67, 89, 101, 156, 203, 234, 345];
  const streetNames = [
    "Main Road",
    "School Street",
    "Gandhi Road",
    "Nehru Street",
    "Park Road",
    "Church Street",
    "Market Road",
    "Station Road",
    "Temple Street",
    "College Road",
  ];

  const streetNumber = streetNumbers[index % streetNumbers.length];
  const streetName = streetNames[index % streetNames.length];

  return `${streetNumber}, ${streetName}, ${city}`;
};

const generateUDISECode = (): string => {
  return "12345678910";
};

const generateGovtSchoolId = (index: number): string => {
  const prefixes = ["123", "456", "789", "101", "102", "103"];
  const prefix = prefixes[index % prefixes.length];
  const number = String(index + 1).padStart(4, "0");

  return `${prefix}${number}`;
};

const generatePincode = (index: number): string => {
  // Generate realistic 6-digit pincodes
  const firstDigit = (Math.floor(index / 100) % 8) + 1; // 1-8
  const remainingDigits = String(index + 10000).slice(-5);

  return `${firstDigit}${remainingDigits}`;
};

// Generate masked phone numbers (only last 4 digits visible, rest are "X")
const generatePhoneNumber = (index: number): string => {
  const lastFourDigits = String(index + 1000).slice(-4);
  return `XXXXXX${lastFourDigits}`;
};

// Generate 1000 school records
const generateMockSchools = (): SchoolData[] => {
  const schools: SchoolData[] = [];

  for (let i = 0; i < 1000; i++) {
    const state = states[i % states.length];
    const district = generateDistrictName(state, i);
    const city = generateCityName(state, i);
    const schoolNameTemplate =
      schoolNameTemplates[i % schoolNameTemplates.length];
    const locationSuffix = locationSuffixes[i % locationSuffixes.length];

    const schoolName = `${schoolNameTemplate} ${locationSuffix}`;
    const udiseCode = generateUDISECode();
    const govtSchoolId = generateGovtSchoolId(i);
    const pincode = generatePincode(i);
    const address = generateAddress(schoolName, city, i);
    const status: "registered" | "new" = i % 4 === 0 ? "registered" : "new";
    const phoneNumber = generatePhoneNumber(i);

    schools.push({
      id: `school_${i + 1}`,
      schoolName,
      udiseCode,
      govtSchoolId,
      state,
      district,
      city,
      pincode,
      address,
      status,
      phoneNumber,
    });
  }

  return schools;
};

// Cache the generated schools to avoid regenerating on every call
let cachedSchools: SchoolData[] | null = null;

const getMockSchools = (): SchoolData[] => {
  if (!cachedSchools) {
    cachedSchools = generateMockSchools();
  }
  return cachedSchools;
};

// Mock API functions
export const mockFetchSchools = async (
  params: SchoolSearchParams = {}
): Promise<PaginatedSchoolResponse> => {
  // Simulate realistic API delay
  await delay(200 + Math.random() * 300);

  const { cursor = 0, limit = 10, search = "" } = params;
  const allSchools = getMockSchools();

  // Filter schools based on search term
  let filteredSchools = allSchools;
  if (search.trim()) {
    const searchLower = search.toLowerCase().trim();
    filteredSchools = allSchools.filter(
      (school) =>
        school.schoolName.toLowerCase().includes(searchLower) ||
        school.udiseCode.includes(searchLower) ||
        school.govtSchoolId.toLowerCase().includes(searchLower) ||
        school.city.toLowerCase().includes(searchLower) ||
        school.district.toLowerCase().includes(searchLower) ||
        school.state.toLowerCase().includes(searchLower) ||
        school.pincode.includes(searchLower)
    );
  }

  // Paginate results
  const startIndex = cursor * limit;
  const endIndex = startIndex + limit;
  const paginatedData = filteredSchools.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    cursor,
    limit,
    total: filteredSchools.length,
    hasMore: endIndex < filteredSchools.length,
  };
};

export const mockFetchSchoolById = async (
  id: string
): Promise<SchoolData | null> => {
  await delay(100);

  const schools = getMockSchools();
  return schools.find((school) => school.id === id) || null;
};

// Utility function for simulating network delay
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Export for testing and development
export { getMockSchools };
